/** Ask the browser to confirm before the tab is closed or reloaded, while something would be lost
 * (E2E P9, G-P9-05; AT-12, warn before loss).
 *
 * An upload is sent from the page, one file after another: closing the tab mid-send abandons the file
 * being sent and every file still queued behind it, and the tab closed with no word. The browser's own
 * "Leave site?" dialog is the one warning a page can raise at that moment - its wording is the
 * browser's and cannot be changed, which is why this says nothing of its own.
 *
 * **Only while `active`.** A handler left on for a page with nothing in flight would put the dialog in
 * front of every reload and every sign-out, and a warning that is always raised is one people stop
 * reading. The listener is on `window`, added when the work starts and removed when it ends or the
 * screen goes, so an upload that finished leaves nothing behind.
 */

import { useEffect } from "react";

export function useLeavingWarning(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const warn = (event: BeforeUnloadEvent) => {
      // The standard spelling; browsers that still want a return value take the empty string.
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [active]);
}
